import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { AddressElement } from '@stripe/react-stripe-js';
import classnames from 'classnames';
import {
  billingAddressOptions,
  setBillingAddressDraft,
} from '../../slices/billingAddress';
import Address from '../forms/AddressForm';
import statesOptions from '../forms/AddressForm/states.json';
import styles from './SelectBillingAddress.module.scss';
import messages from './messages';
import validator from './validator';
import { selectShippingAddress } from '../../store/selectors';
import Checkbox from '../common/Checkbox';
import { STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE } from '../../utils/common/constants';

const initialValues = {
  email: '',
  phone: '',
  firstName: '',
  lastName: '',
  streetAddress: '',
  additionalStreetInfo: '',
  postalCode: '',
  city: '',
  country: 'US',
  state: statesOptions[0].value,
};

const SelectBillingAddress = ({
  isActive,
  onValidateChange,
  isTouched,
  addressOptions,
}) => {
  const { formatMessage } = useIntl();
  const { billingAddress } = useSelector((state) => state.cart);
  const shippingAddress = useSelector(selectShippingAddress);
  const [isBillingAddressSame, setIsBillingAddressSame] = useState(true);

  const isStripeRegularCheckoutEnabled = window.purTogglesEnabled(
    STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE
  );

  const dispatch = useDispatch();

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    touched,
    setTouched,
    validateForm,
  } = useFormik({
    initialValues: billingAddress
      ? {
          ...initialValues,
          ...billingAddress,
          streetAddress: billingAddress.streetName || '',
        }
      : initialValues,
    validate: (v) => validator(v, { formatMessage }),
  });

  useEffect(() => {
    const validationResult = validator(values, { formatMessage });
    onValidateChange({
      isValid: Object.values(validationResult).length === 0,
      errors: validationResult,    
    });
    if (Object.values(validationResult).length === 0) {
      const addressValues = {
        email: values.email,
        phone: values.phone,
        firstName: values.firstName,
        lastName: values.lastName,
        streetAddress: values.streetAddress,
        additionalStreetInfo: values.additionalStreetInfo,
        postalCode: values.postalCode,
        city: values.city,
        country: values.country,
        state: values.state,
      }
      dispatch(setBillingAddressDraft(addressValues));
    }
  }, [dispatch, values, formatMessage, validateForm, onValidateChange]);

  useEffect(() => {
    if (isTouched) {
      setTouched(
        Object.keys(values).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      );
      validateForm(values);
    }
  }, [isTouched, setTouched, validateForm, values]);

  useEffect(() => {
    if (isBillingAddressSame) {
      dispatch(setBillingAddressDraft(shippingAddress));
    }
  }, [dispatch, isBillingAddressSame, shippingAddress]);

  const handleOnStripeAddressElementChange = (event) => {
    const stripeBillingAddressDraft = {
      email: billingAddress.email,
      phone: billingAddress.phone,
      firstName: event.value.firstName,
      lastName: event.value.lastName,
      streetAddress: event.value.address.line1,
      additionalStreetInfo: event.value.address.line2,
      postalCode: event.value.address.postal_code,
      city: event.value.address.city,
      country: event.value.address.country,
      state: event.value.address.state,
    };
    dispatch(setBillingAddressDraft(stripeBillingAddressDraft));
  };

  return (
    isActive && (
      <div
        data-testid="select-billing-address"
        className={classnames(styles['billing-address-container'], {
          [styles['is-stripe-billing']]: isStripeRegularCheckoutEnabled
        })}
      >
        <div className={styles['billing-address-options-group']}>
          <Checkbox
            id={billingAddressOptions.same}
            name="billing-address-option"
            label={formatMessage(messages.sameBilling)}
            onChange={() => setIsBillingAddressSame(!isBillingAddressSame)}
            checked={isBillingAddressSame}
            dataTestId={billingAddressOptions.same}
          />
        </div>
        {!isBillingAddressSame && !isStripeRegularCheckoutEnabled && (
          <Address
            prefix="billing"
            handleChange={handleChange}
            handleBlur={handleBlur}
            values={values}
            isShowingContactDetails={false}
            isAddressAutocomplete
            onAddressAutocomplete={setFieldValue}
            onFieldValueSet={setFieldValue}
            touched={touched}
            errors={errors}
          />
        )}
        {!isBillingAddressSame && isStripeRegularCheckoutEnabled && (
          <AddressElement
            options={addressOptions}
            onChange={handleOnStripeAddressElementChange}
          />
        )}
      </div>
    )
  );
};

SelectBillingAddress.propTypes = {
  isActive: PropTypes.bool.isRequired,
  isTouched: PropTypes.bool,
  onValidateChange: PropTypes.func.isRequired,
  addressOptions: PropTypes.object,
};

SelectBillingAddress.defaultProps = {
  isTouched: false,
  addressOptions: {},
};

export default SelectBillingAddress;
